import { inject, Injectable } from '@angular/core';
import { AuthService } from '../../authentication/auth.service';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from 'firebase/auth';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AccountDataService {
  private http = inject(HttpClient);
  private auth = inject(AuthService);

  /**
   * given a dictionary containing a stripe credit card temporary token (a string) returns a promise to post this data to the backend
   */
  upgradeAccountToPro(creditCardDict: Object): Observable<any> {
    return this.http.post(
      `${environment.baseApiUrl}upgrade_account_to_pro/`,
      creditCardDict
    );
  }

  /**
   * returns a promise to mark the account as deleted in the backend SQL database
   */
  markAccountAsDeletedInSQL(): Observable<any> {
    return this.http.post(
      `${environment.baseApiUrl}mark_account_as_deleted/`,
      []
    );
  }

  /**
   * returns a promise to delete this user's account in firebase authentication
   */
  deleteUserAccountInFirebase(): Promise<any> {
    const firebaseUser = this.auth.getCurrentUser();
    return firebaseUser.delete();
  }

  /**
   * given an old password and a new password (both strings), returns a promise to update this user's account password to it in firebase authentication after verifying/reauthenticating with old password
   */
  changeAccountPasswordInFirebase(
    oldPassword: string,
    newPassword: string
  ): Promise<any> {
    const firebaseUser = this.auth.getCurrentUser();
    const credential = EmailAuthProvider.credential(
      firebaseUser.email,
      oldPassword
    );

    return reauthenticateWithCredential(firebaseUser, credential).then(() =>
      updatePassword(firebaseUser, newPassword)
    );
  }

  /**
   * given a dictionary containing a stripe credit card temporary token (a string) returns a promise to update this user's credit card details in the backend
   */
  updateCredCardDetails(creditCardDict: Object): Observable<any> {
    return this.http.post(
      `${environment.baseApiUrl}update_credit_card_details/`,
      creditCardDict
    );
  }

  /**
   * returns a promise to deactivate Pro from this account in the backend
   */
  deactivateProForThisAccount(): Observable<any> {
    return this.http.post(
      `${environment.baseApiUrl}deactivate_pro_for_this_account/`,
      []
    );
  }

  /**
   * returns a promise to make a request to the backend for the current user's dashboard data, including current items
   */
  getNewestAccountData() {
    return this.http.get<any>(`${environment.baseApiUrl}fetch_account_data/`);
  }

  /**
   * given an updated intro tutorial opt out preference, return a promise to save it to the backend
   */
  updateWelcomePopupPreference(newOptOutPref: boolean): Observable<any> {
    return this.http.post(
      `${environment.baseApiUrl}update_welcome_dialog_opt_out_preference/`,
      newOptOutPref
    );
  }

  /**
   * given an updated dashboard theme, return a promise to save it to the backend
   */
  saveDashboardTheme(theme: string): Observable<any> {
    const payload = { dashboardTheme: theme };

    return this.http.post(
      `${environment.baseApiUrl}update_dashboard_theme/`,
      payload
    );
  }

  /**
   * given an array of dictionaries representing the newest unsaved item changes, return a promise to make these changes in the backend
   */
  saveUnsavedChanges(unsavedChanges: Object[]): Observable<any> {
    const payload = { unsavedChanges: unsavedChanges };

    return this.http.post(
      `${environment.baseApiUrl}save_newest_changes/`,
      payload
    );
  }
}
